import { useEffect, useState } from 'react'
import {
  type AreaBrief,
  type KnowledgeItem,
  knowledgeConsolidationApi,
} from '../../api/knowledgeConsolidation'
import { ItemDetail } from './ItemDetail'
import { SourcesTab } from './SourcesTab'

const kinds: Record<string, string | undefined> = {
  Features: 'feature',
  Requirements: 'requirement',
  'Business Rules': 'business_rule',
  Data: 'data_entity',
  Decisions: 'decision',
  Dependencies: 'dependency',
  Conflicts: 'conflict',
}

export function AreaBriefView({
  projectId,
  areaId,
  tab,
  refreshKey,
  fallbackOverview,
}: {
  projectId: number
  areaId: number
  tab: string
  refreshKey: number
  fallbackOverview?: string | null
}) {
  const [brief, setBrief] = useState<AreaBrief | null>(null)
  const [items, setItems] = useState<KnowledgeItem[]>([])
  const [selected, setSelected] = useState<KnowledgeItem | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void refreshKey
    let active = true
    setError(null)
    Promise.all([
      knowledgeConsolidationApi.getBrief(projectId, areaId),
      knowledgeConsolidationApi.listItems(projectId, areaId, kinds[tab]),
    ])
      .then(([nextBrief, nextItems]) => {
        if (active) {
          setBrief(nextBrief)
          setItems(nextItems)
        }
      })
      .catch((cause) => active && setError(cause instanceof Error ? cause.message : String(cause)))
    return () => {
      active = false
    }
  }, [projectId, areaId, tab, refreshKey])

  const openItem = async (item: KnowledgeItem) => {
    setSelected(await knowledgeConsolidationApi.getItem(projectId, item.id))
  }
  if (error)
    return tab === 'Overview' && fallbackOverview ? (
      <p className="text-harbor-text-secondary">{fallbackOverview}</p>
    ) : (
      <p className="text-red-400">{error}</p>
    )
  if (!brief)
    return (
      <p className="text-harbor-text-muted">
        {tab === 'Overview' && fallbackOverview
          ? fallbackOverview
          : 'Loading consolidated knowledge…'}
      </p>
    )
  if (tab === 'Overview')
    return <p className="whitespace-pre-wrap text-harbor-text-secondary">{brief.overview}</p>
  if (tab === 'Sources')
    return <SourcesTab evidenceIds={[...new Set(items.flatMap((item) => item.evidenceIds))]} />
  const groupedItems =
    tab === 'Requirements'
      ? Object.entries(
          items.reduce<Record<string, KnowledgeItem[]>>((groups, item) => {
            const group = item.requirementType ?? 'UNCLASSIFIED'
            const groupItems = groups[group] ?? []
            groupItems.push(item)
            groups[group] = groupItems
            return groups
          }, {})
        ).sort(([left], [right]) => left.localeCompare(right))
      : [['', items] as const]
  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-harbor-text-muted">No consolidated items in this category.</p>
      )}
      {groupedItems.map(([group, groupItems]) => (
        <section key={group || 'items'} className="space-y-2">
          {group && (
            <h3 className="pt-2 text-xs font-semibold uppercase tracking-wide text-harbor-text-muted">
              {group
                .toLowerCase()
                .split('_')
                .join(' ')
                .replace(/^./, (letter: string) => letter.toUpperCase())}
            </h3>
          )}
          {groupItems?.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openItem(item)}
              className="w-full rounded-lg border border-harbor-border p-3 text-left hover:bg-harbor-surface-raised"
            >
              <span className="font-medium text-harbor-text">{item.title}</span>
              {item.attributes?.gap ? (
                <span className="ml-2 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300">
                  gap
                </span>
              ) : null}
              {item.statement && (
                <span className="mt-1 block text-harbor-text-secondary">{item.statement}</span>
              )}
            </button>
          ))}
        </section>
      ))}
      <ItemDetail item={selected} onClose={() => setSelected(null)} />
    </div>
  )
}
